<template>
    <filter-panel
        :filterOptions="filterOptions"
        :model-value="filterState"
        @update:modelValue="onUpdateFilters"
    />
    <v-data-table
        :headers="experimentGroupHeaders"
        :items="filteredExperimentGroups"
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
        :search="search"
    >
        <template v-slot:item.actions="{ item }">
            <v-row>
                <v-icon @click="viewExperimentGroup(item)">$eye</v-icon>
                <v-icon @click="deleteExperimentGroup(item)">$delete</v-icon>
            </v-row>
        </template>
    </v-data-table>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { ExperimentGroupDTO } from "@/services/experimentGroups/experimentGroupsAPI";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { useAuth } from "@/lib/ts/auth";
import FilterPanel from "@/components/common/FilterPanel.vue";
import {
    buildFilterOptionsFromData,
    FilterConfigItem,
    filterData,
    FilterOptions,
} from "@/lib/filterUtils";
import { useRouter } from "vue-router";

const auth = useAuth();
const router = useRouter();

const userCanManageExperimentGroups = computed(() => {
    return auth.isAuthorized("ExperimentGroups:Manage");
});

interface Props {
    experimentGroups: ExperimentGroupDTO[];
    search: string | undefined;
}

const props = withDefaults(defineProps<Props>(), {
    search: "",
});

const filterState = ref(<Record<string, string[]>>{});

const filterConfig = ref<FilterConfigItem[]>([
    { name: "name", prop: "name" },
    { name: "objective", prop: "objective", noneOption: true },
    { name: "schemaConfig", prop: "schemaConfigName", noneOption: true },
    { name: "experimentMethod", prop: "experimentMethodName", noneOption: true},
    { name: "availableInExperimentSetup", prop: "isAvailableInExperimentSetup", defaults: [true]}
]);

const filterOptions = ref<FilterOptions>({});

const filteredExperimentGroups = computed(() => {
    return filterData(
        props.experimentGroups,
        filterState.value,
        filterConfig.value
    ) as ExperimentGroupDTO[];
});

watch(
    () => props.experimentGroups,
    async (newExperimentGroups) => {
        filterOptions.value = buildFilterOptionsFromData(newExperimentGroups, filterConfig.value);
    },
    { immediate: true }
);

const emit = defineEmits<{
    (e: "deleteExperimentGroup", item: ExperimentGroupDTO): void;
}>();

const experimentGroupHeaders = ref<VDataTableHeader[]>([
    {
        title: "Code",
        value: "code",
        sortable: true,
        align: "start",
        width: "5%",
    },
    {
        title: "Name",
        value: "name",
        sortable: true,
        align: "start",
        width: "25%",
    },
    {
        title: "Platform",
        value: "platform.name",
        sortable: true,
        align: "start",
        width: "10%",
    },
    {
        title: "Objective",
        value: "objective",
        sortable: true,
        align: "start",
        width: "10%",
    },
    {
        title: "Schema Config",
        value: "schemaConfig.name",
        align: "start",
        width: "20%",
    },
    {
        title: "Experiment Method",
        value: "experimentMethod.name",
        align: "start",
        width: "20%",
    },
    {
        title: "Available in Experiment Setup?",
        value: "isAvailableInExperimentSetup",
        align: "start",
        width: "10%"
    },
]);

if (userCanManageExperimentGroups.value) {
    experimentGroupHeaders.value.push(
        {
            title: "Actions",
            value: "actions",
            sortable: false,
            align: "start",
            width: "5%",
        }
    );
}

function viewExperimentGroup(experimentGroup: ExperimentGroupDTO) {
    router.push({ path: `/experiment-groups/${experimentGroup.code}` });
}

function deleteExperimentGroup(item: ExperimentGroupDTO) {
    emit("deleteExperimentGroup", item);
}

function onUpdateFilters(newFilters: Record<string, string[]>) {
    filterState.value = newFilters;
}
</script>
